<template>
  <div class="timeline-container" :class="{ 'reduce-photo-motion': shouldReducePhotoMotion }">
    <!-- Timeline Manager Link (hidden for shared timelines) -->
    <div v-if="!isSharedTimeline" class="timeline-header-bar">
      <router-link to="/dashboard" class="btn-manage-timelines">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
        Back to Dashboard
      </router-link>
    </div>

    <h1 class="timeline-title" style="font-family: 'PringleandTweed'">{{ currentTimelineName || 'Our Time' }}</h1>

    <!-- Share Timeline Button (hidden for shared timelines) -->
    <button v-if="!isSharedTimeline && timelines.length > 0" @click="showShareModal()" class="btn-share-timeline" title="Share Timeline">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="18" cy="5" r="3"></circle>
        <circle cx="6" cy="12" r="3"></circle>
        <circle cx="18" cy="19" r="3"></circle>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
      </svg>
      <span class="button-text">Share</span>
    </button>

    <!-- Floating Add Memory Button (hidden for view-only shared timelines) -->
    <button v-if="isEditable" @click="showMemoryModal()" class="floating-add-button" title="Add Memory">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      <span class="button-text">Add Memory</span>
    </button>

    <!-- Create Your Own Timeline Button (only for shared timelines) -->
    <router-link v-if="isSharedTimeline" to="/register" class="floating-cta-button" title="Create Your Own Timeline">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <span class="button-text">Create Your Own Timeline</span>
    </router-link>

    <div class="timeline">
      <div v-for="(memory, index) in memories" :key="memory.id" class="timeline-item" :class="{ 'timeline-item-left': index % 2 === 0, 'timeline-item-right': index % 2 !== 0 }">

        <!-- Timeline dot and line -->
        <div class="timeline-marker">
          <div class="timeline-dot"></div>
        </div>

        <!-- Memory content -->
        <div class="timeline-content" :class="{ 'view-only': isViewOnly }">
          <!-- Date badge -->
          <div class="timeline-date-badge" @click="isEditable ? showMemoryModal(memory) : null" :class="{ 'clickable': isEditable }" v-if="memory.date">
            <span class="date-day">{{ new Date(memory.date).getDate() }}</span>
            <span class="date-month">{{ new Date(memory.date).toLocaleDateString('en-US', { month: 'short' }) }}</span>
            <span class="date-year">{{ new Date(memory.date).getFullYear() }}</span>
          </div>

          <!-- Photo gallery -->
          <div class="memory-photos" v-if="memory.photo || memory.photo2 || memory.photo3 || memory.photo4 || memory.photo5">
            <div class="photo-grid" :class="galleryClass(memory)">
              <img v-if="memory.photo || memory.photo_display" :src="getDisplayPhoto(memory, 'photo')" alt="Memory photo" class="memory-photo" @click="openPhotoViewer(getOriginalPhoto(memory, 'photo'))" @load="handleImageLoad" loading="lazy" decoding="async" fetchpriority="low">
              <img v-if="memory.photo2 || memory.photo2_display" :src="getDisplayPhoto(memory, 'photo2')" alt="Memory photo 2" class="memory-photo" @click="openPhotoViewer(getOriginalPhoto(memory, 'photo2'))" @load="handleImageLoad" loading="lazy" decoding="async" fetchpriority="low">
              <img v-if="memory.photo3 || memory.photo3_display" :src="getDisplayPhoto(memory, 'photo3')" alt="Memory photo 3" class="memory-photo" @click="openPhotoViewer(getOriginalPhoto(memory, 'photo3'))" @load="handleImageLoad" loading="lazy" decoding="async" fetchpriority="low">
              <img v-if="memory.photo4 || memory.photo4_display" :src="getDisplayPhoto(memory, 'photo4')" alt="Memory photo 4" class="memory-photo" @click="openPhotoViewer(getOriginalPhoto(memory, 'photo4'))" @load="handleImageLoad" loading="lazy" decoding="async" fetchpriority="low">
              <img v-if="memory.photo5 || memory.photo5_display" :src="getDisplayPhoto(memory, 'photo5')" alt="Memory photo 5" class="memory-photo" @click="openPhotoViewer(getOriginalPhoto(memory, 'photo5'))" @load="handleImageLoad" loading="lazy" decoding="async" fetchpriority="low">
            </div>
          </div>

          <!-- Memory details -->
          <div class="memory-details" @click="isEditable ? showMemoryModal(memory) : null" :class="{ 'clickable': isEditable }">
            <h2 class="memory-title" style="font-family: 'PringleandTweed'">{{ memory.name }}</h2>
            <p class="memory-description">{{ memory.description }}</p>
            <div class="memory-location-badge" v-if="memory.location">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span>{{ memory.location }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal name="memory-modal" width="90%" height="auto" :scrollable='true' style="z-index: 9999;">
      <div class="memory-modal-container">
        <!-- Modal Header -->
        <div class="modal-header-custom">
          <h2 class="modal-title" style="font-family: 'PringleandTweed'">
            <svg v-if="!memory.id" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2v20M2 12h20"></path>
            </svg>
            <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            {{ !memory.id ? 'Add New Memory' : 'Edit Memory' }}
          </h2>
          <button class="modal-close-btn" @click="$modal.hide('memory-modal')">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body-custom">
          <!-- Photos Section -->
          <div class="form-section">
            <h3 class="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              Photos
            </h3>

            <div class="photos-grid">
              <div class="photo-upload-item" v-for="index in 5" :key="index">
                <label :for="'photo' + (index === 1 ? '' : index)" class="photo-upload-label">
                  <div v-if="memory['photo' + (index === 1 ? '' : index)] || (index === 1 ? photoPreview : index === 2 ? photoPreview2 : index === 3 ? photoPreview3 : index === 4 ? photoPreview4 : photoPreview5)" class="photo-preview">
                    <img :src="memory['photo' + (index === 1 ? '' : index)] || (index === 1 ? photoPreview : index === 2 ? photoPreview2 : index === 3 ? photoPreview3 : index === 4 ? photoPreview4 : photoPreview5)" alt="Preview">
                    <div class="photo-overlay">
                      <button type="button" class="photo-delete-btn" @click.prevent="deletePhoto('photo' + (index === 1 ? '' : index))">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                      <span class="change-text">Change</span>
                    </div>
                  </div>
                  <div v-else class="photo-upload-placeholder">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                    <span>Photo {{ index }}</span>
                  </div>
                  <input
                    type="file"
                    class="photo-input-hidden"
                    :id="'photo' + (index === 1 ? '' : index)"
                    :ref="'photo' + (index === 1 ? '' : index)"
                    @change="handleFileUpload()"
                    accept="image/*"
                  />
                </label>
              </div>
            </div>
          </div>

          <!-- Basic Info Section -->
          <div class="form-section">
            <h3 class="section-title">Basic Details</h3>
            <div class="form-row">
              <div class="form-group full-width">
                <label class="form-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                  </svg>
                  Memory Title
                </label>
                <input type="text" class="form-input" v-model="name" placeholder="Give this memory a name...">
              </div>
            </div>

            <div class="form-row" v-if="!$route.query.timelineID && !isSharedTimeline">
              <div class="form-group full-width">
                <label class="form-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  Timeline
                </label>
                <select class="form-input" v-model="selectedTimeline">
                  <option :value="null">No Timeline</option>
                  <option v-for="timeline in timelines" :key="timeline.id" :value="timeline.id">
                    {{ timeline.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                Description
              </label>
              <textarea v-model="description" rows="4" class="form-textarea" placeholder="Tell the story of this memory..."></textarea>
            </div>

            <div class="form-row two-cols">
              <div class="form-group">
                <label class="form-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  Location
                </label>
                <input type="text" class="form-input" v-model="location" placeholder="Where was this?">
              </div>

              <div class="form-group">
                <label class="form-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  Date
                </label>
                <datepicker class="form-datepicker" v-model="date"></datepicker>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer-custom">
          <button v-if="!memory.id" class="btn-primary" @click="createMemory()" :disabled="isCreatingMemory">
            <svg v-if="!isCreatingMemory" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            <div v-else class="spinner"></div>
            {{ isCreatingMemory ? 'Creating...' : 'Create Memory' }}
          </button>
          <template v-else>
            <button class="btn-danger" @click="showEraseMemoryConfirmationModal()" :disabled="isCreatingMemory">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              Delete Memory
            </button>
            <button class="btn-primary" @click="updateMemory()" :disabled="isCreatingMemory">
              <svg v-if="!isCreatingMemory" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              <div v-else class="spinner"></div>
              {{ isCreatingMemory ? 'Saving...' : 'Save Changes' }}
            </button>
          </template>
        </div>
      </div>
    </modal>

    <modal name="erase-memory-confirmation-modal" width="500px" height="auto" :scrollable='true' style="z-index: 10000;">
      <div class="confirmation-modal-container">
        <div class="confirmation-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h2 class="confirmation-title">Delete Memory?</h2>
        <p class="confirmation-message">Are you sure you want to delete this memory? This action cannot be undone and the memory will be lost forever.</p>
        <div class="confirmation-buttons">
          <button class="btn-cancel" @click="$modal.hide('erase-memory-confirmation-modal')">
            Cancel
          </button>
          <button class="btn-confirm-delete" @click="eraseMemory()">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Yes, Delete
          </button>
        </div>
      </div>
    </modal>

    <modal name="share-modal" width="600px" height="auto" :scrollable='true' style="z-index: 10000;">
      <div class="share-modal-container">
        <div class="modal-header-custom">
          <h2 class="modal-title" style="font-family: 'PringleandTweed'">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
            Share Timeline
          </h2>
          <button class="modal-close-btn" @click="$modal.hide('share-modal')">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="modal-body-custom">
          <!-- Timeline Selector -->
          <div class="form-group" v-if="timelines.length > 1">
            <label class="form-label">Select Timeline to Share</label>
            <select class="form-input" v-model="timelineToShare">
              <option v-for="timeline in timelines" :key="timeline.id" :value="timeline.id">
                {{ timeline.name }}
              </option>
            </select>
          </div>

          <div class="share-divider"></div>

          <div class="share-section">
            <h3 class="share-section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              Editable Share Link
            </h3>
            <p class="share-description">Anyone with this link can view and edit memories in this timeline</p>
            <div class="share-link-container">
              <input
                type="text"
                class="share-link-input"
                :value="editShareLink"
                readonly
                ref="editLinkInput"
              />
              <button class="btn-copy" @click="copyLink('edit')">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                {{ editLinkCopied ? 'Copied!' : 'Copy' }}
              </button>
              <button class="btn-regenerate" @click="regenerateToken('edit')">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                </svg>
                Regenerate
              </button>
            </div>
          </div>

          <div class="share-section">
            <h3 class="share-section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              View-Only Share Link
            </h3>
            <p class="share-description">Anyone with this link can only view memories (no editing)</p>
            <div class="share-link-container">
              <input
                type="text"
                class="share-link-input"
                :value="viewShareLink"
                readonly
                ref="viewLinkInput"
              />
              <button class="btn-copy" @click="copyLink('view')">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                {{ viewLinkCopied ? 'Copied!' : 'Copy' }}
              </button>
              <button class="btn-regenerate" @click="regenerateToken('view')">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                </svg>
                Regenerate
              </button>
            </div>
          </div>

          <div class="share-note">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span>Regenerating a link will invalidate the old link. Anyone using the old link will lose access.</span>
          </div>
        </div>
      </div>
    </modal>

    <modal name="photo-viewer-modal" width="90%" height="90%" :scrollable='true' style="z-index: 10000;">
      <div class="photo-viewer-container">
        <button class="photo-viewer-close" @click="closePhotoViewer()">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <img :src="selectedPhoto" alt="Full size photo" class="photo-viewer-image">
      </div>
    </modal>
  </div>
</template>

<script>
import axios from 'axios'
import Datepicker from 'vuejs-datepicker'; // https://www.npmjs.com/package/vuejs-datepicker

export default {
  props: ['userID', 'token', 'accessType'],
  components: {
    Datepicker,
  },
  data() {
    return {
      user: {},

      memories: [],
      photos: [],
      timelines: [],
      currentTimelineName: '',

      memory: {},

      name: '',
      description: '',
      location: '',
      date: null,
      photo: null,
      photo2: null,
      photo3: null,
      photo4: null,
      photo5: null,
      photoPreview: '',
      photoPreview2: '',
      photoPreview3: '',
      photoPreview4: '',
      photoPreview5: '',
      selectedTimeline: null,
      currentTimelineData: null,

      // Share link state
      timelineToShare: null,
      editShareLink: '',
      viewShareLink: '',
      editLinkCopied: false,
      viewLinkCopied: false,

      // photoWidth: "200px",
      photoWidth: 200,

      selectedPhoto: '',
      isCreatingMemory: false,
    }
  },
  created() {
    if (this.isSharedTimeline) {
      this.loadSharedTimeline()
    } else {
      this.getUser()
    }
  },
  computed: {
    isSharedTimeline() {
      return !!this.token && !!this.accessType
    },
    isViewOnly() {
      return this.isSharedTimeline && this.accessType === 'view'
    },
    isEditable() {
      return !this.isViewOnly
    },
    totalPhotoCount() {
      return this.memories.reduce((total, memory) => {
        const count = [memory.photo, memory.photo2, memory.photo3, memory.photo4, memory.photo5].filter(Boolean).length
        return total + count
      }, 0)
    },
    shouldReducePhotoMotion() {
      return this.totalPhotoCount >= 60
    }
  },
  watch: {
    timelineToShare(newVal, oldVal) {
      // Only reload if modal is open and value actually changed
      if (newVal && oldVal && newVal !== oldVal) {
        this.loadShareLinks()
      }
    }
  },
  methods: {
    loadSharedTimeline() {
      axios.get(`/api/timeline/shared/${this.token}/${this.accessType}/`)
        .then(res => {
          this.currentTimelineName = res.data.timeline.name
          this.currentTimelineData = res.data.timeline
          // Store user and timeline info for creating/editing memories
          this.user = { id: res.data.timeline.user }
          this.selectedTimeline = res.data.timeline.id
          return axios.get(`/api/timeline/shared/${this.token}/${this.accessType}/memories/`)
        })
        .then(res => {
          this.memories = res.data.memories.sort((a, b) => (a.date > b.date) ? -1 : 1)
        })
        .catch(error => {
          console.error('Error loading shared timeline:', error)
          alert('This timeline link is invalid or has been regenerated.')
        })
    },
    reloadMemories() {
      if (this.isSharedTimeline) {
        this.loadSharedTimeline()
      } else {
        this.getMemories()
      }
    },
    getUser() {
      console.log('Getting user')

      // if (!this.userID) {
      //   this.$store.dispatch('getUser').then(() => { // Lecture 360
      //     this.user = this.$store.getters.user
      //     console.log(this.$store.getters.user)
      //     this.getMemories()
      //   })
      // }
      // else {
      //   axios.get('/api/users/?id='.concat(this.userID))
      //     .then(res => {
      //       this.user = res.data[0]
      //       this.getMemories()
      //     })
      //     .catch(error => console.log(error))
      // }

      this.$store.dispatch('getUser').then(() => { // Lecture 360
        this.user = this.$store.getters.user
        console.log(this.$store.getters.user)
        this.getTimelines()
      })
    },
    getTimelines() {
      axios.get(`/api/timelines/?user=${this.user.id}`)
        .then(res => {
          this.timelines = res.data

          const timelineID = this.$route.query.timelineID
          if (timelineID) {
            const parsedTimelineId = parseInt(timelineID, 10)
            this.selectedTimeline = parsedTimelineId
            this.timelineToShare = parsedTimelineId
          } else if (!this.selectedTimeline && this.timelines.length > 0) {
            this.selectedTimeline = this.timelines[0].id
            this.timelineToShare = this.timelines[0].id
          }

          // After timelines are loaded, get memories (so we can set the timeline name)
          this.getMemories()
        })
        .catch(error => console.log(error))
    },
    getMemories() {
      // If it's a shared timeline, memories are already loaded
      if (this.isSharedTimeline) {
        return
      }

      console.log(this.userID)
      console.log(this.$store.getters.userId)
      console.log(this.user.id)

      // Get timelineID from query params if it exists
      const timelineID = this.$route.query.timelineID

      if (timelineID) {
        const parsedTimelineId = parseInt(timelineID, 10)
        this.selectedTimeline = parsedTimelineId
        this.timelineToShare = parsedTimelineId
      }

      let url = `/api/memories/?user=${this.user.id}`

      // If a specific timeline is selected, filter by that timeline
      if (timelineID) {
        url += `&timeline=${timelineID}`

        // Find and set the timeline name
        const timeline = this.timelines.find(t => t.id == timelineID)
        if (timeline) {
          this.currentTimelineName = timeline.name
        } else {
          // Fallback: fetch the timeline directly if not found in the list
          axios.get(`/api/timelines/${timelineID}/`)
            .then(res => {
              this.currentTimelineName = res.data.name
            })
            .catch(error => console.log('Error fetching timeline:', error))
        }
      } else {
        // No specific timeline selected, reset the name
        this.currentTimelineName = ''
      }

      axios.get(url)
        .then(res => {
          this.memories = res.data
          this.memories = this.memories.sort((a, b) => (a.date > b.date) ? -1 : 1)
          console.log(this.memories)
        })
        .catch(error => console.log(error))
    },
    showMemoryModal(memory) {
      // Don't allow editing in view-only mode
      if (this.isViewOnly) {
        return
      }

      // Reset loading state
      this.isCreatingMemory = false

      if (!memory) {
        this.name = ''
        this.description = ''
        this.location = ''
        this.date = null
        this.photo = null
        this.photo2 = null
        this.photo3 = null
        this.photo4 = null
        this.photo5 = null
        this.photoPreview = ''
        this.photoPreview2 = ''
        this.photoPreview3 = ''
        this.photoPreview4 = ''
        this.photoPreview5 = ''
        // Automatically set the timeline to the current timeline if viewing a specific timeline
        // For shared timelines, keep the selectedTimeline that was set in loadSharedTimeline()
        if (!this.isSharedTimeline) {
          const timelineID = this.$route.query.timelineID
          this.selectedTimeline = timelineID ? parseInt(timelineID) : null
        }
        // If shared timeline, selectedTimeline is already set, so don't override it
        this.memory = {}
        this.$modal.show('memory-modal')
      }
      else {
        this.name = memory.name
        this.description = memory.description
        this.location = memory.location
        this.date = memory.date
        this.photo = null
        this.photo2 = null
        this.photo3 = null
        this.photo4 = null
        this.photo5 = null
        this.photoPreview = ''
        this.photoPreview2 = ''
        this.photoPreview3 = ''
        this.photoPreview4 = ''
        this.photoPreview5 = ''
        this.selectedTimeline = memory.timeline
        this.memory = memory
        this.$modal.show('memory-modal')
      }
    },
    handleFileUpload() {
      // Since refs are in a v-for loop, they're arrays - access the first element
      if (this.$refs.photo && this.$refs.photo[0] && this.$refs.photo[0].files[0]) {
        this.photo = this.$refs.photo[0].files[0];
        this.photoPreview = URL.createObjectURL(this.photo);
      }
      if (this.$refs.photo2 && this.$refs.photo2[0] && this.$refs.photo2[0].files[0]) {
        this.photo2 = this.$refs.photo2[0].files[0];
        this.photoPreview2 = URL.createObjectURL(this.photo2);
      }
      if (this.$refs.photo3 && this.$refs.photo3[0] && this.$refs.photo3[0].files[0]) {
        this.photo3 = this.$refs.photo3[0].files[0];
        this.photoPreview3 = URL.createObjectURL(this.photo3);
      }
      if (this.$refs.photo4 && this.$refs.photo4[0] && this.$refs.photo4[0].files[0]) {
        this.photo4 = this.$refs.photo4[0].files[0];
        this.photoPreview4 = URL.createObjectURL(this.photo4);
      }
      if (this.$refs.photo5 && this.$refs.photo5[0] && this.$refs.photo5[0].files[0]) {
        this.photo5 = this.$refs.photo5[0].files[0];
        this.photoPreview5 = URL.createObjectURL(this.photo5);
      }
    },
    createMemory() {
      this.isCreatingMemory = true
      axios.post(`/api/memories/`, {
        name: this.name,
        description: this.description,
        location: this.location,
        date: this.date,
        user: this.user.id,
        timeline: this.selectedTimeline,
      })
        .then(res => {
          let formData = new FormData();
          if (this.photo) {
            formData.append("photo", this.photo);
          }
          if (this.photo2) {
            formData.append("photo2", this.photo2);
          }
          if (this.photo3) {
            formData.append("photo3", this.photo3);
          }
          if (this.photo4) {
            formData.append("photo4", this.photo4);
          }
          if (this.photo5) {
            formData.append("photo5", this.photo5);
          }

          axios.patch(`/api/memories/${res.data.id}/`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data"
              }
            }
          )
            .then(res => {
              this.isCreatingMemory = false
              this.$modal.hide('memory-modal')
              this.reloadMemories()
            })
            .catch(error => {
              this.isCreatingMemory = false
              console.log(error)
            })

        })
        .catch(error => {
          this.isCreatingMemory = false
          console.log(error)
        })
    },
    updateMemory() {
      this.isCreatingMemory = true
      axios.patch(`/api/memories/${this.memory.id}/`, {
        name: this.name,
        description: this.description,
        location: this.location,
        date: this.date,
        timeline: this.selectedTimeline
      })
        .then(res => {
          let formData = new FormData();
          if (this.photo) {
            formData.append("photo", this.photo);
          }
          if (this.photo2) {
            formData.append("photo2", this.photo2);
          }
          if (this.photo3) {
            formData.append("photo3", this.photo3);
          }
          if (this.photo4) {
            formData.append("photo4", this.photo4);
          }
          if (this.photo5) {
            formData.append("photo5", this.photo5);
          }

          axios.patch(`/api/memories/${this.memory.id}/`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data"
              }
            }
          )
            .then(res => {
              this.isCreatingMemory = false
              this.$modal.hide('memory-modal')
              this.getMemories()
            })
            .catch(error => {
              this.isCreatingMemory = false
              console.log(error)
            })

        })
        .catch(error => {
          this.isCreatingMemory = false
          console.log(error)
        })
    },
    showEraseMemoryConfirmationModal() {
      this.$modal.show('erase-memory-confirmation-modal')
    },
    eraseMemory() {
      axios.delete(`/api/memories/${this.memory.id}/`)
        .then(res => {
          this.$modal.hide('memory-modal')
          this.$modal.hide('erase-memory-confirmation-modal')
          this.reloadMemories()
        })
    },
    galleryClass(memory) {
      const photoCount = [memory.photo, memory.photo2, memory.photo3, memory.photo4, memory.photo5].filter(Boolean).length

      if (photoCount === 4) {
        return 'photo-grid-4'
      }

      if (photoCount === 5) {
        return 'photo-grid-5'
      }

      if (photoCount === 1) {
        return 'photo-grid-1'
      }

      if (photoCount === 2) {
        return 'photo-grid-2'
      }

      if (photoCount === 3) {
        return 'photo-grid-3'
      }

      return 'photo-grid-many'
    },
    getDisplayPhoto(memory, sourceField) {
      const displayField = `${sourceField}_display`
      return memory[displayField] || memory[sourceField] || ''
    },
    getOriginalPhoto(memory, sourceField) {
      return memory[sourceField] || memory[`${sourceField}_display`] || ''
    },
    galleryWidth(memory) {
      let galleryWidth = 0
      let multiplier = 1.1
      if (memory.photo) {
        galleryWidth += this.photoWidth*multiplier
      }
      if (memory.photo2) {
        galleryWidth += this.photoWidth*multiplier
      }
      if (memory.photo3) {
        galleryWidth += this.photoWidth*multiplier
      }
      if (memory.photo4) {
        galleryWidth += this.photoWidth*multiplier
      }
      if (memory.photo5) {
        galleryWidth += this.photoWidth*multiplier
      }
      // return 800;
      return galleryWidth
    },
    deletePhoto(photoField) {
      // Clear the preview and file if it's a newly selected photo (not yet saved)
      if (photoField === 'photo') {
        this.photo = null;
        this.photoPreview = '';
      } else if (photoField === 'photo2') {
        this.photo2 = null;
        this.photoPreview2 = '';
      } else if (photoField === 'photo3') {
        this.photo3 = null;
        this.photoPreview3 = '';
      } else if (photoField === 'photo4') {
        this.photo4 = null;
        this.photoPreview4 = '';
      } else if (photoField === 'photo5') {
        this.photo5 = null;
        this.photoPreview5 = '';
      }

      // If editing an existing memory with saved photos, delete from server
      if (this.memory.id && this.memory[photoField]) {
        let formData = new FormData();
        formData.append(`${photoField}`, '');

        axios.patch(`/api/memories/${this.memory.id}/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }
        )
          .then(res => {
            this.memory[photoField] = '';
            this.reloadMemories()
          })
          .catch(error => {
            console.log(error)
          })
      }
    },
    openPhotoViewer(photoUrl) {
      this.selectedPhoto = photoUrl
      this.$modal.show('photo-viewer-modal')
    },
    closePhotoViewer() {
      this.$modal.hide('photo-viewer-modal')
      this.selectedPhoto = ''
    },
    handleImageLoad(event) {
      const img = event.target
      const aspectRatio = img.naturalWidth / img.naturalHeight

      // Wider images scroll horizontally, taller/square images scroll vertically
      if (aspectRatio >= 1.4) {
        // Wide landscape images - scroll horizontally
        img.classList.add('landscape')
        img.classList.remove('portrait')
      } else {
        // Portrait, square, or slightly wide images - scroll vertically
        img.classList.add('portrait')
        img.classList.remove('landscape')
      }
    },
    showShareModal() {
      const currentTimelineId = this.$route.query.timelineID ? parseInt(this.$route.query.timelineID, 10) : this.selectedTimeline

      if (!this.timelineToShare || this.timelineToShare !== currentTimelineId) {
        this.timelineToShare = currentTimelineId || (this.timelines.length > 0 ? this.timelines[0].id : null)
      }

      if (this.timelineToShare) {
        this.loadShareLinks()
      } else {
        alert('No timeline available to share')
      }
    },
    loadShareLinks() {
      axios.get(`/api/timelines/${this.timelineToShare}/`)
        .then(res => {
          this.currentTimelineData = res.data
          this.editShareLink = res.data.edit_share_link || ''
          this.viewShareLink = res.data.view_share_link || ''
          this.editLinkCopied = false
          this.viewLinkCopied = false
          this.$modal.show('share-modal')
        })
        .catch(error => {
          console.error('Error fetching timeline:', error)
          alert('Failed to load share links')
        })
    },
    copyLink(type) {
      let input
      if (type === 'edit') {
        input = this.$refs.editLinkInput
        this.editLinkCopied = true
        setTimeout(() => {
          this.editLinkCopied = false
        }, 2000)
      } else {
        input = this.$refs.viewLinkInput
        this.viewLinkCopied = true
        setTimeout(() => {
          this.viewLinkCopied = false
        }, 2000)
      }

      input.select()
      document.execCommand('copy')
    },
    regenerateToken(type) {
      if (!confirm(`Are you sure you want to regenerate the ${type === 'edit' ? 'edit' : 'view-only'} link? This will invalidate the old link.`)) {
        return
      }

      const endpoint = type === 'edit' ? 'regenerate_edit_token' : 'regenerate_view_token'
      const timelineId = this.timelineToShare || this.selectedTimeline

      if (!timelineId) {
        alert('No timeline selected')
        return
      }

      axios.post(`/api/timelines/${timelineId}/${endpoint}/`)
        .then(res => {
          this.editShareLink = res.data.edit_share_link || ''
          this.viewShareLink = res.data.view_share_link || ''
          alert(`${type === 'edit' ? 'Edit' : 'View-only'} link has been regenerated successfully`)
        })
        .catch(error => {
          console.error('Error regenerating token:', error)
          alert('Failed to regenerate link')
        })
    }
  }
}
</script>

<style scoped>
.timeline-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  padding-top: 100px;
}

.timeline-header-bar {
  display: flex;
  justify-content: flex-end;
  padding: 20px 0;
  position: relative;
  z-index: 10;
}

.btn-manage-timelines {
  background: linear-gradient(135deg, #702670 0%, #9b4d96 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(112, 38, 112, 0.3);
  position: relative;
  z-index: 10;
}

.btn-manage-timelines:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(112, 38, 112, 0.4);
  color: white;
  text-decoration: none;
}

.timeline-title {
  text-align: center;
  margin-top: 2rem;
  margin-bottom: 3rem;
  font-size: 3rem;
  color: #333;
}

/* Floating Add Button */
.floating-add-button {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  z-index: 1000;
  transition: all 0.3s ease;
  overflow: hidden;
}

.floating-add-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.5);
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.floating-add-button:active {
  transform: translateY(-1px);
}

.floating-add-button svg {
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.floating-add-button .button-text {
  white-space: nowrap;
}

/* Floating CTA Button (Create Your Own Timeline) */
.floating-cta-button {
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(135deg, #702670 0%, #9b4d96 100%);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(112, 38, 112, 0.4);
  z-index: 999;
  transition: all 0.3s ease;
  text-decoration: none;
}

.floating-cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(112, 38, 112, 0.5);
  background: linear-gradient(135deg, #9b4d96 0%, #702670 100%);
  text-decoration: none;
}

.floating-cta-button:active {
  transform: translateY(-1px);
}

.floating-cta-button svg {
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.floating-cta-button .button-text {
  white-space: nowrap;
}

.timeline {
  position: relative;
  padding: 50px 0;
}

/* Vertical line down the center */
.timeline::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #e0e0e0, #a0a0a0, #e0e0e0);
  transform: translateX(-50%);
}

.timeline-item {
  position: relative;
  width: 100%;
  margin-bottom: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  content-visibility: auto;
  contain-intrinsic-size: 480px;
}

.timeline-marker {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.timeline-dot {
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: 4px solid white;
  border-radius: 50%;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.timeline-item:hover .timeline-dot {
  transform: scale(1.3);
  box-shadow: 0 0 0 6px rgba(102, 126, 234, 0.3), 0 6px 20px rgba(0, 0, 0, 0.25);
}

.timeline-content {
  position: relative;
  width: 45%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.timeline-content:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

/* Alternating left and right */
.timeline-item-left .timeline-content {
  margin-right: auto;
  margin-left: 0;
}

.timeline-item-right .timeline-content {
  margin-left: auto;
  margin-right: 0;
}

/* Date badge */
.timeline-date-badge {
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 10px 16px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.timeline-item-right .timeline-date-badge {
  right: auto;
  left: 20px;
  bottom: 20px;
}

.date-day {
  font-size: 1rem;
  font-weight: 700;
}

.date-month {
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.date-year {
  font-size: 1rem;
  font-weight: 600;
}

/* Photo grid */
.memory-photos {
  width: 100%;
  background: #f8f9fa;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
  padding: 8px;
}

.photo-grid-4 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(130px, 1fr));
}

.photo-grid-4 .memory-photo {
  height: 100%;
  min-height: 150px;
}

.photo-grid-5 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(120px, 1fr));
}

.photo-grid-5 .memory-photo:nth-child(1) {
  grid-column: 1;
  grid-row: 1;
}

.photo-grid-5 .memory-photo:nth-child(2) {
  grid-column: 2;
  grid-row: 1;
}

.photo-grid-5 .memory-photo:nth-child(3) {
  grid-column: 3;
  grid-row: 1;
}

.photo-grid-5 .memory-photo:nth-child(4) {
  grid-column: 1 / span 2;
  grid-row: 2;
}

.photo-grid-5 .memory-photo:nth-child(5) {
  grid-column: 3;
  grid-row: 2;
}

.photo-grid-3 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.photo-grid-3 .memory-photo:first-child {
  grid-column: 1 / -1;
  height: 220px;
}

.photo-grid-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.photo-grid-1 .memory-photo {
  height: 250px;
}

.photo-grid-many {
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}

@keyframes scrollPhotoVertical {
  0%, 100% {
    object-position: center top;
  }
  50% {
    object-position: center bottom;
  }
}

@keyframes scrollPhotoHorizontal {
  0%, 100% {
    object-position: left center;
  }
  50% {
    object-position: right center;
  }
}

.memory-photo {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  transition: transform 0.3s ease;
}

.memory-photo.portrait {
  animation: scrollPhotoVertical 8s ease-in-out infinite;
}

.memory-photo.landscape {
  animation: scrollPhotoHorizontal 10s ease-in-out infinite;
}

.reduce-photo-motion .memory-photo.portrait,
.reduce-photo-motion .memory-photo.landscape {
  animation-timing-function: linear;
}

.reduce-photo-motion .memory-photo.portrait {
  animation-duration: 16s;
}

.reduce-photo-motion .memory-photo.landscape {
  animation-duration: 20s;
}

.memory-photo:hover {
  animation-play-state: paused;
  transform: scale(1.05);
}

/* Memory details */
.memory-details {
  padding: 25px 30px;
  position: relative;
  padding-bottom: 75px;
}

.memory-title {
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 16px;
  line-height: 1.3;
}

.memory-description {
  color: #555;
  line-height: 1.6;
  font-size: 1rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  margin-bottom: 10px;
}

.memory-location-badge {
  position: absolute;
  bottom: 20px;
  left: 25px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f8f9fa;
  padding: 8px 14px;
  border-radius: 20px;
  color: #667eea;
  font-size: 0.85rem;
  font-weight: 600;
  border: 1px solid #e8e9ef;
}

.timeline-item-right .memory-location-badge {
  left: auto;
  right: 25px;
}

.memory-location-badge svg {
  flex-shrink: 0;
  stroke: #667eea;
}

/* Responsive design */
@media (max-width: 768px) {
  .timeline::before {
    left: 30px;
  }

  .timeline-marker {
    left: 30px;
  }

  .timeline-item {
    margin-bottom: 50px;
  }

  .timeline-content {
    width: calc(100% - 80px);
    margin-left: 80px !important;
    margin-right: 0 !important;
  }

  .timeline-date-badge {
    bottom: 15px;
    left: 15px !important;
    right: auto !important;
    padding: 8px 12px;
    gap: 5px;
  }

  .date-day,
  .date-month,
  .date-year {
    font-size: 0.9rem;
  }

  .memory-details {
    padding: 20px;
    padding-bottom: 100px;
  }

  .memory-title {
    font-size: 1.4rem;
  }

  .memory-location-badge {
    bottom: 65px;
    left: 15px !important;
    right: auto !important;
    font-size: 0.8rem;
    padding: 6px 12px;
  }

  .timeline-item-right .timeline-date-badge {
    left: 15px !important;
    right: auto !important;
  }

  .timeline-item-right .memory-location-badge {
    left: 15px !important;
    right: auto !important;
  }

  .photo-grid {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  }

  .memory-photo {
    height: 150px;
  }

  .floating-add-button {
    bottom: 20px;
    right: 20px;
    padding: 14px 20px;
    font-size: 0.95rem;
  }

  .floating-cta-button {
    bottom: 20px;
    left: 20px;
    padding: 14px 20px;
    font-size: 0.95rem;
  }
}

@media (max-width: 480px) {
  .timeline-title {
    font-size: 2rem;
  }

  .timeline-content {
    width: calc(100% - 60px);
    margin-left: 60px !important;
  }

  .floating-add-button {
    bottom: 15px;
    right: 15px;
    padding: 16px;
    border-radius: 50%;
    width: 56px;
    height: 56px;
    justify-content: center;
  }

  .floating-add-button .button-text {
    display: none;
  }

  .floating-add-button svg {
    margin: 0;
  }

  .floating-cta-button {
    bottom: 15px;
    left: 15px;
    padding: 12px 16px;
    font-size: 0.85rem;
  }

  .floating-cta-button .button-text {
    font-size: 0.85rem;
  }
}

/* Photo viewer styles */
.photo-viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.95);
  padding: 20px;
}

.photo-viewer-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.photo-viewer-close:hover {
  background: white;
  transform: rotate(90deg);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.photo-viewer-close svg {
  stroke: #333;
}

.photo-viewer-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

/* Cursor pointer for photos */
.memory-photo {
  cursor: pointer;
}

/* Date badge cursor */
.timeline-date-badge {
  cursor: pointer;
}

/* Memory details cursor */
.memory-details {
  cursor: pointer;
}

/* Memory Modal Styles */
.memory-modal-container {
  background: white;
  overflow: hidden;
  margin: 0;
  width: 100%;
}

.modal-header-custom {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-title svg {
  flex-shrink: 0;
}

.modal-close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.modal-body-custom {
  padding: 30px;
  max-height: 70vh;
  overflow-y: auto;
}

.form-section {
  margin-bottom: 32px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 1.3rem;
  color: #2c3e50;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
}

.section-title svg {
  color: #667eea;
}

.form-row {
  display: grid;
  gap: 20px;
  margin-bottom: 20px;
}

.form-row.two-cols {
  grid-template-columns: 1fr 1fr;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  width: 100%;
}

.form-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-label svg {
  color: #667eea;
  flex-shrink: 0;
}

.form-input,
.form-textarea {
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  font-family: inherit;
  width: 100%;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.form-datepicker {
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  width: 100%;
}

.form-datepicker:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Photos Grid */
.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
}

.photo-upload-item {
  position: relative;
}

.photo-upload-label {
  display: block;
  cursor: pointer;
  border-radius: 12px;
  overflow: hidden;
  background: #f8f9fa;
  border: 2px dashed #d0d0d0;
  transition: all 0.3s ease;
}

.photo-upload-label:hover {
  border-color: #667eea;
  background: #f0f3ff;
}

.photo-upload-placeholder {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #999;
  padding: 20px;
}

.photo-upload-placeholder svg {
  opacity: 0.5;
}

.photo-upload-placeholder span {
  font-size: 0.9rem;
  font-weight: 500;
}

.photo-preview {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
}

.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.photo-preview:hover .photo-overlay {
  opacity: 1;
}

.photo-delete-btn {
  background: #e74c3c;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
}

.photo-delete-btn:hover {
  background: #c0392b;
  transform: scale(1.1);
}

.change-text {
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
}

.photo-input-hidden {
  display: none;
}

/* Modal Footer */
.modal-footer-custom {
  padding: 20px 30px;
  background: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border-top: 1px solid #e0e0e0;
}

.modal-footer-custom > .btn-primary:only-child {
  margin-left: auto;
}

.btn-primary,
.btn-danger {
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-danger {
  background: #e74c3c;
  color: white;
}

.btn-danger:hover {
  background: #c0392b;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(231, 76, 60, 0.4);
}

.btn-primary:disabled,
.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

/* Loading Spinner */
.spinner {
  width: 18px;
  height: 18px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Modal */
@media (max-width: 768px) {
  .modal-header-custom {
    padding: 20px;
  }

  .modal-title {
    font-size: 1.4rem;
  }

  .modal-body-custom {
    padding: 20px;
  }

  .form-row.two-cols {
    grid-template-columns: 1fr;
  }

  .photos-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
  }

  .modal-footer-custom {
    padding: 16px 20px;
    flex-direction: column;
    gap: 12px;
  }

  .modal-footer-custom > .btn-primary:only-child {
    margin-left: 0;
  }

  .btn-primary,
  .btn-danger {
    width: 100%;
    justify-content: center;
  }
}

/* Confirmation Modal Styles */
.confirmation-modal-container {
  background: white;
  border-radius: 16px;
  padding: 40px;
  text-align: center;
}

.confirmation-icon {
  width: 80px;
  height: 80px;
  background: #fee;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.confirmation-icon svg {
  color: #e74c3c;
}

.confirmation-title {
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 16px;
  font-weight: 700;
}

.confirmation-message {
  font-size: 1.05rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 32px;
}

.confirmation-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-cancel,
.btn-confirm-delete {
  padding: 12px 28px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-cancel {
  background: #e0e0e0;
  color: #555;
}

.btn-cancel:hover {
  background: #d0d0d0;
}

.btn-confirm-delete {
  background: #e74c3c;
  color: white;
}

.btn-confirm-delete:hover {
  background: #c0392b;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(231, 76, 60, 0.4);
}

@media (max-width: 576px) {
  .confirmation-modal-container {
    padding: 30px 20px;
  }

  .confirmation-buttons {
    flex-direction: column;
    width: 100%;
  }

  .btn-cancel,
  .btn-confirm-delete {
    width: 100%;
    justify-content: center;
  }
}

/* Share Timeline Button */
.btn-share-timeline {
  position: fixed;
  bottom: 110px;
  right: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 14px 22px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 25px rgba(118, 75, 162, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
}

.btn-share-timeline:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 10px 35px rgba(118, 75, 162, 0.5);
}

/* Share Modal */
.share-modal-container {
  background: white;
  border-radius: 0 0 16px 16px;
  max-height: 90vh;
  overflow: hidden;
}

.share-divider {
  height: 1px;
  background: #e7e7e7;
  margin: 24px 0 28px;
}

.share-section {
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid #f0f0f0;
}

.share-section:last-of-type {
  border-bottom: none;
}

.share-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #702670;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 8px;
  justify-content: flex-start;
}

.share-description {
  color: #666;
  font-size: 0.95rem;
  margin-bottom: 16px;
  text-align: left;
  line-height: 1.5;
}

.share-link-container {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.share-link-input {
  flex: 1;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #666;
  background: #f9f9f9;
  font-family: monospace;
}

.btn-copy,
.btn-regenerate {
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-copy {
  background: linear-gradient(135deg, #702670 0%, #9b4d96 100%);
  color: white;
}

.btn-copy:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(112, 38, 112, 0.3);
}

.btn-regenerate {
  background: #f0f0f0;
  color: #666;
}

.btn-regenerate:hover {
  background: #e0e0e0;
  color: #333;
}

.share-note {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  border-radius: 8px;
  margin-top: 20px;
}

.share-note svg {
  flex-shrink: 0;
  color: #ffc107;
  margin-top: 2px;
}

.share-note span {
  color: #856404;
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Access Badge for Shared Timelines */
.access-badge-container {
  text-align: center;
  margin-bottom: 20px;
}

.access-badge {
  display: inline-block;
  padding: 10px 24px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.badge-edit {
  background: linear-gradient(135deg, #702670 0%, #9b4d96 100%);
  color: white;
}

.badge-view {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: white;
}

/* View-only mode styling */
.timeline-content.view-only {
  cursor: default;
}

.timeline-content.view-only:hover {
  transform: none;
}

.clickable {
  cursor: pointer;
}

.memory-details.clickable:hover {
  opacity: 0.9;
}

@media (max-width: 768px) {
  .share-link-container {
    flex-direction: column;
  }

  .btn-copy,
  .btn-regenerate {
    width: 100%;
    justify-content: center;
  }

  .btn-share-timeline {
    bottom: 110px;
    right: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Timeline.vue